import { proxy, transfer, wrap } from 'comlink';
import { createWorker } from '../../utils/workerUtils';
import { AbstractVideoEngine } from './AbstractVideoEngine';
import { SafariVideoDecoder } from './SafariVideoDecoder';
import { Timekeeper } from './Timekeeper';
export class StandardVideoEngine extends AbstractVideoEngine {
    LOG_PREFIX = '[StandardVideoEngine]';
    extractorWorker;
    extractorWorkerProxy;
    safariVideoDecoder;
    // TODO: temporarily disabled SafariVideoDecoder — revert to re-enable
    useWebRTCDecoder = false; // 'RTCRtpScriptTransform' in globalThis;
    constructor() {
        super();
        console.log(`${this.LOG_PREFIX} constructor called`);
        if (this.useWebRTCDecoder) {
            console.log(`${this.LOG_PREFIX} RTCRtpScriptTransform available, will use WebRTC decoder path`);
        }
    }
    // ========== Safari WebRTC decoder overrides ==========
    async startVideoDecoderWorker() {
        if (!this.useWebRTCDecoder) {
            return super.startVideoDecoderWorker();
        }
        if (this.videoDecoderWorkerProxy) {
            console.warn(`${this.LOG_PREFIX} videoDecoderWorker already running`);
            return;
        }
        if (!this.videoPlayerWorkerProxy) {
            throw new Error('videoPlayerWorker must be running before starting videoDecoderWorker');
        }
        console.log(`${this.LOG_PREFIX} startVideoDecoderWorker (Safari WebRTC path)`);
        this.safariVideoDecoder = new SafariVideoDecoder();
        await this.safariVideoDecoder.init();
        // Assign as the decoder proxy so all inherited delegation works
        this.videoDecoderWorkerProxy = this
            .safariVideoDecoder;
        const decoderPlayerChannel = new MessageChannel();
        this.safariVideoDecoder.connectPlayerPort(decoderPlayerChannel.port1);
        await this.videoPlayerWorkerProxy.connectDecoderPort(transfer(decoderPlayerChannel.port2, [decoderPlayerChannel.port2]));
        console.log(`${this.LOG_PREFIX} videoDecoderWorker started (Safari WebRTC path)`);
    }
    async stopVideoDecoderWorker() {
        if (!this.safariVideoDecoder) {
            return super.stopVideoDecoderWorker();
        }
        console.log(`${this.LOG_PREFIX} stopVideoDecoderWorker (Safari WebRTC path)`);
        try {
            await this.safariVideoDecoder.destroy();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} SafariVideoDecoder destroy failed`, error);
        }
        this.safariVideoDecoder = undefined;
        this.videoDecoderWorkerProxy = undefined;
        this.videoDecoderWorker = undefined;
        console.log(`${this.LOG_PREFIX} videoDecoderWorker stopped (Safari WebRTC path)`);
    }
    async onVideoEncoderWorkerStarted() {
        this.extractorWorker = this.createExtractorWorker();
        this.extractorWorkerProxy = wrap(this.extractorWorker);
        const extractorEncoderChannel = new MessageChannel();
        await this.extractorWorkerProxy.connectEncoderPort(transfer(extractorEncoderChannel.port1, [
            extractorEncoderChannel.port1,
        ]));
        await this.videoEncoderWorkerProxy?.connectExtractorPort(transfer(extractorEncoderChannel.port2, [
            extractorEncoderChannel.port2,
        ]));
        await this.extractorWorkerProxy.setGetLastTimestampCallback(proxy(this.getLastTimestamp));
    }
    async onVideoEncoderWorkerStopping() {
        try {
            await this.extractorWorkerProxy?.destroy();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} extractorWorker destroy failed`, error);
        }
        this.extractorWorker?.terminate();
        this.extractorWorker = undefined;
        this.extractorWorkerProxy = undefined;
    }
    async addMediaStreamVideoTrack(track) {
        console.log(`${this.LOG_PREFIX} addMediaStreamVideoTrack called`, {
            trackId: track.id,
        });
        await this.extractorWorkerProxy?.addMediaStreamVideoTrack(transfer(track, [track]));
    }
    async removeMediaStreamVideoTrack(track) {
        console.log(`${this.LOG_PREFIX} removeMediaStreamVideoTrack called`, {
            trackId: track.id,
        });
        await this.extractorWorkerProxy?.removeMediaStreamVideoTrack(track.id);
        console.log(`${this.LOG_PREFIX} removeMediaStreamVideoTrack completed`, {
            trackId: track.id,
        });
    }
    async setScreenOrientation(type, angle) {
        await super.setScreenOrientation(type, angle);
        await this.extractorWorkerProxy?.setScreenOrientation(type, angle);
    }
    createExtractorWorker() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            console.log(`${this.LOG_PREFIX} creating extractor worker with URL constructor`);
            return new Worker(new URL('./VideoExtractorWorker.ts', import.meta.url), { type: 'module' });
        }
        console.log(`${this.LOG_PREFIX} creating extractor worker with createWorker util`);
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./VideoExtractorWorker${ext}`, import.meta.url);
    }
    getLastTimestamp = async () => {
        const timestamp = Timekeeper.getInstance('producer').getLastTimestampMicros();
        console.log(`${this.LOG_PREFIX} getLastTimestamp returned timestamp from Timekeeper`, {
            timestamp: timestamp.toString(),
        });
        return timestamp;
    };
}
